import { Sparkles, RefreshCw, AlertTriangle, CheckCircle2, MessageCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/ToastProvider'
import {
  useAiAccountantPrep,
  useAiGenerateAccountantPrep,
  useAiGenerateMonthlySummary,
  useAiMonthlySummary,
  useAiStatus,
} from '@/lib/api/hooks'
import type { AiAccountantPrep, AiMonthlySummary } from '@/lib/api/types'
import { Link } from 'react-router-dom'

/**
 * MonthlyClosePage tetejére kerül — két AI-generált blokk:
 *  - Havi vezetői összefoglaló (narrative + highlights)
 *  - Kérdések a könyvelőhöz (mit gondolj át mielőtt elküldöd a csomagot)
 */
export function MonthlyAiPanel({ year, month, companyId }: { year: number; month: number; companyId: string }) {
  const { data: aiState } = useAiStatus()
  const summaryQ = useAiMonthlySummary(year, month, companyId)
  const prepQ = useAiAccountantPrep(year, month, companyId)
  const genSummary = useAiGenerateMonthlySummary()
  const genPrep = useAiGenerateAccountantPrep()
  const toast = useToast()

  if (!aiState?.enabled) {
    return (
      <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-3 py-2 text-xs text-slate-600 dark:text-slate-300 flex items-center gap-2 mb-4">
        <Sparkles className="h-3.5 w-3.5 text-slate-400" />
        <span>
          AI insights kikapcsolva. Az <code>OPENAI_API_KEY</code> backend env-re állításával bekapcsolható.
        </span>
      </div>
    )
  }

  async function regenSummary() {
    try {
      await genSummary.mutateAsync({ year, month, companyId })
      toast.show({ tone: 'success', title: 'AI összefoglaló kész' })
    } catch (err) {
      toast.show({ tone: 'error', title: 'Hiba', description: err instanceof Error ? err.message : 'Ismeretlen' })
    }
  }
  async function regenPrep() {
    try {
      await genPrep.mutateAsync({ year, month, companyId })
      toast.show({ tone: 'success', title: 'Könyvelői kérdések kész' })
    } catch (err) {
      toast.show({ tone: 'error', title: 'Hiba', description: err instanceof Error ? err.message : 'Ismeretlen' })
    }
  }

  const summary = summaryQ.data?.result?.payload as unknown as AiMonthlySummary | undefined
  const prep = prepQ.data?.result?.payload as unknown as AiAccountantPrep | undefined

  return (
    <div className="space-y-3 mb-4">
      {/* Havi vezetői összefoglaló */}
      <div className="rounded-md border border-brand-200 dark:border-brand-900/50 bg-gradient-to-r from-brand-50/60 to-sky-50/40 dark:from-brand-950/30 dark:to-sky-950/20 px-3 py-3">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="text-sm font-medium text-brand-800 dark:text-brand-200 flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> AI vezetői összefoglaló
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={regenSummary}
            loading={genSummary.isPending}
            leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
          >
            {summary ? 'Frissít' : 'Generálás'}
          </Button>
        </div>
        {summaryQ.isLoading ? (
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Spinner size={12} /> Töltés…
          </div>
        ) : summary ? (
          <>
            <p className="text-sm text-slate-800 dark:text-slate-100 leading-relaxed">{summary.narrative}</p>
            {summary.highlights.length > 0 && (
              <ul className="mt-2 space-y-1">
                {summary.highlights.map((h, i) => (
                  <li key={i} className="text-xs text-slate-700 dark:text-slate-300 flex items-start gap-1.5">
                    <CheckCircle2 className="h-3 w-3 mt-0.5 flex-shrink-0 text-brand-500" />
                    {h}
                  </li>
                ))}
              </ul>
            )}
            {summary.recommendation && (
              <div className="mt-2 text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 px-2 py-1.5 rounded border border-amber-200 dark:border-amber-900/50 flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                <span>{summary.recommendation}</span>
              </div>
            )}
          </>
        ) : (
          <p className="text-xs text-slate-500">Még nem generáltunk összefoglalót erre a hónapra.</p>
        )}
      </div>

      {/* Könyvelői prep kérdések */}
      <div className="rounded-md border border-indigo-200 dark:border-indigo-900/50 bg-indigo-50/60 dark:bg-indigo-950/20 px-3 py-3">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="text-sm font-medium text-indigo-800 dark:text-indigo-200 flex items-center gap-2">
            <MessageCircle className="h-4 w-4" /> Kérdések a könyvelőhöz (mielőtt elküldöd)
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={regenPrep}
            loading={genPrep.isPending}
            leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
          >
            {prep ? 'Frissít' : 'Generálás'}
          </Button>
        </div>
        {prepQ.isLoading ? (
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Spinner size={12} /> Töltés…
          </div>
        ) : prep ? (
          prep.questions.length === 0 ? (
            <p className="text-xs text-slate-600 dark:text-slate-300">{prep.summary || 'Minden tiszta — küldheted a csomagot.'}</p>
          ) : (
            <>
              {prep.summary && <p className="text-xs text-slate-700 dark:text-slate-200 mb-2">{prep.summary}</p>}
              <ol className="space-y-2">
                {prep.questions.map((q, i) => (
                  <li key={q.invoiceId + i} className="text-sm">
                    <div className="text-slate-800 dark:text-slate-100">
                      <span className="text-indigo-600 dark:text-indigo-300 font-mono text-xs mr-1">{i + 1}.</span>
                      {q.question}
                    </div>
                    {q.why && <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 ml-4">→ {q.why}</div>}
                    {q.invoiceId && (
                      <Link
                        to={`/invoices/${q.invoiceId}`}
                        className="text-[11px] underline text-brand-600 dark:text-brand-300 ml-4"
                      >
                        ugrás a számlához →
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
            </>
          )
        ) : (
          <p className="text-xs text-slate-500">Még nem generáltunk kérdéseket erre a hónapra.</p>
        )}
      </div>
    </div>
  )
}
